import { useMount } from 'ahooks';
import { useAppDispatch } from 'app/hooks';
import { RevxSkeleton } from 'components';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { MultiTypeEnum, SKU } from 'domains/skuDomain';
import { FileItem } from 'domains/workspaceDetailDomain';
import { JobItem } from 'domains/workspaceDomain';
import { useUser } from 'hooks';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import skuService from 'services/skuService';
import { isSourcer, isSupplier } from 'utils/rbac';
import { CommonFilesSubmit, CommonUploadFile } from '../../common';
import { useDefaultFileList } from '../../common/UploadFile/useDefaultFileList';
import useShowUploadFileFlag from 'features/Workspace/hooks/useShowUploadFileFlag';
import './Files.less';

const Files = (jobData: JobItem) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const statusFilters = useJobStatusCollection();
  const [fileList, setFileList] = useState<FileItem[]>();
  const [defaultFileList, setDefaultFileList] = useState<FileItem[]>();
  const [isConfirm, setArtworkConfirm] = useState<boolean>(
    jobData.artworkConfirm
  );
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const user = useUser();
  const [sku, setSku] = useState<SKU>();
  const isWrapperSku = sku?.multiType === MultiTypeEnum.WRAPPER;
  const getSkuDefaultFileList = useDefaultFileList(defaultFileList || []);

  const showUploadAction = useShowUploadFileFlag(jobData);

  useMount(async () => {
    setLoading(true);
    const fileListRes = await dispatch.workspaceDetail.getAllFile(jobData);
    setFileList(fileListRes);
    setDefaultFileList(fileListRes);
    setArtworkConfirm(jobData.artworkConfirm);
    const skuDetail = await skuService.startViewSku(
      {
        id: jobData.skuId,
      },
      true
    );

    setSku(skuDetail);
    setLoading(false);
    dispatch.workspace.getJobItemCount(jobData.id);
  });

  const fileSubmit = async () => {
    dispatch.app.showLoading();
    try {
      await dispatch.workspaceDetail.fileSubmit(jobData);
      const jobItem = await workspaceService.getJobDetail(jobData.id);
      dispatch.workspace.getJobCountByStatusFilter(statusFilters);
      dispatch.workspace.reloadJobList();
      setArtworkConfirm(jobItem.artworkConfirm);
      dispatch.workspace.getJobItemCount(jobData.id);
    } catch (e) {}
    dispatch.app.hideLoading();
  };

  const onUploadFileChange = async () => {
    const fileListRes = await dispatch.workspaceDetail.getAllFile(jobData);
    setFileList(fileListRes);
  };

  const hasFiles = defaultFileList && defaultFileList?.length > 0;
  const renderHeader = () => {
    if (isSourcer(user)) {
      if (hasFiles) {
        return <span>{t('workspace.detail.sourcerFiles.awaitSubmit')}</span>;
      }
      return <span>{t('workspace.detail.sourcerFiles.awaitFiles')}</span>;
    } else if (isSupplier(user)) {
      return <span>{t('workspace.detail.supplierFiles.awaitFiles')}</span>;
    }
  };

  return (
    <div className="files-card">
      <RevxSkeleton loading={loading}>
        <header>
          {Boolean(!isConfirm && !showUploadAction) ? (
            <RevxFlexLayout justify="justify-center">
              {renderHeader()}
            </RevxFlexLayout>
          ) : (
            <></>
          )}
          {Boolean(!isConfirm && showUploadAction) ? (
            <>
              <p>{t('workspace.detail.files.attachFiles')}</p>
              <span hidden={isConfirm}>
                {t('workspace.detail.files.attachFilesTip')}:
              </span>
            </>
          ) : (
            <></>
          )}
          {Boolean(isConfirm) ? (
            <p>{t('workspace.detail.supplierFiles.title')}</p>
          ) : (
            <></>
          )}
        </header>

        {isWrapperSku ? (
          <div>
            {(sku?.parts || []).map((partSku) => {
              return (
                <div className="add-files-section">
                  <div className="add-files-item-title">
                    {partSku.part.name}
                  </div>
                  {getSkuDefaultFileList(partSku.part.id)?.length ||
                  showUploadAction ? (
                    <div style={{ paddingLeft: 20 }}>
                      <CommonUploadFile
                        showUploadButton={showUploadAction}
                        defaultFileList={getSkuDefaultFileList(partSku.part.id)}
                        itemData={jobData}
                        deleteFileHandler={dispatch.workspaceDetail.deleteFile}
                        getAllFileHandler={dispatch.workspaceDetail.getAllFile}
                        saveFileHandler={async (item) => {
                          const fileItem =
                            await dispatch.workspaceDetail.saveFile({
                              ...item,
                              skuId: partSku.part.id,
                            });
                          return fileItem;
                        }}
                        onUploadFileChange={onUploadFileChange}
                        setIsUploading={setIsUploading}
                      />
                    </div>
                  ) : (
                    <span>{t('workspace.detail.files.noArtfileUploaded')}</span>
                  )}
                </div>
              );
            })}
            <div className="add-files-section">
              <div className="add-files-item-title">Other Files</div>
              {getSkuDefaultFileList()?.length || showUploadAction ? (
                <div style={{ paddingLeft: 20 }}>
                  <CommonUploadFile
                    showUploadButton={showUploadAction}
                    defaultFileList={getSkuDefaultFileList()}
                    itemData={jobData}
                    deleteFileHandler={dispatch.workspaceDetail.deleteFile}
                    getAllFileHandler={dispatch.workspaceDetail.getAllFile}
                    saveFileHandler={dispatch.workspaceDetail.saveFile}
                    onUploadFileChange={onUploadFileChange}
                    setIsUploading={setIsUploading}
                  />
                </div>
              ) : (
                <span>{t('workspace.detail.files.noArtfileUploaded')}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="add-files-section">
            <CommonUploadFile
              showUploadButton={showUploadAction}
              defaultFileList={defaultFileList}
              itemData={jobData}
              deleteFileHandler={dispatch.workspaceDetail.deleteFile}
              getAllFileHandler={dispatch.workspaceDetail.getAllFile}
              saveFileHandler={dispatch.workspaceDetail.saveFile}
              onUploadFileChange={onUploadFileChange}
              setIsUploading={setIsUploading}
            />
          </div>
        )}

        {showUploadAction && !isConfirm && (
          <CommonFilesSubmit
            text={t('workspace.detail.files.submitFiles')}
            disabled={!fileList?.length || isUploading}
            onClick={() => fileSubmit()}
          />
        )}
      </RevxSkeleton>
    </div>
  );
};

export default Files;
